import type {
  MarketRegionItem,
  MarketRegionListPage,
  MarketRegionMemberItem,
  MarketRegionMemberRole,
  MarketRegionOrganizationOption,
  MarketRegionOrganizationUnitOption,
  MarketRegionScopeItem,
  MarketRegionScopeType,
  MarketRegionStaffOption,
  MarketRegionType,
} from "../types";

export interface MarketRegionRaw {
  id: number;
  region_name: string;
  region_code?: string | null;
  region_type: MarketRegionType;
  description?: string | null;
  is_disabled: boolean;
  is_disabled_label?: string;
  created_at?: string;
  updated_at?: string;
  remark?: string | null;
}

export interface MarketRegionListPageRaw {
  items: MarketRegionRaw[];
  total: number;
  page: number;
  page_size: number;
}

export interface MarketRegionScopeRaw {
  id: number;
  region_id: number;
  scope_type: MarketRegionScopeType;
  country?: string | null;
  province?: string | null;
  city?: string | null;
  org_id?: number | null;
  org_unit_id?: number | null;
  customer_id?: number | null;
  contact_id?: number | null;
  scope_name_snapshot?: string | null;
  is_excluded: boolean;
  is_excluded_label?: string;
  created_at?: string;
  remark?: string | null;
}

export interface MarketRegionMemberRaw {
  id: number;
  region_id: number;
  user_id: number;
  user_name?: string | null;
  member_role: MarketRegionMemberRole;
  can_assign: boolean;
  can_edit_scope: boolean;
  can_view_stats: boolean;
  is_disabled: boolean;
  is_disabled_label?: string;
  created_at?: string;
  updated_at?: string;
  remark?: string | null;
}

interface SysStaffRaw {
  id: number;
  username: string;
  realname?: string | null;
  nickname?: string | null;
  main_dept_name?: string | null;
  status: number;
}

export interface SysStaffListPayloadRaw {
  items?: SysStaffRaw[];
}

export interface MarketRegionOrganizationRaw {
  id: number;
  org_name: string;
  province?: string | null;
  city?: string | null;
}

export interface MarketRegionOrganizationListPayloadRaw {
  items?: MarketRegionOrganizationRaw[];
}

export interface MarketRegionOrganizationUnitRaw {
  id: number;
  org_id: number;
  unit_name: string;
  unit_path_name?: string | null;
  level_no?: number | null;
}

/** 将后端区域经营配置行转换为前端列表项。 */
export function mapMarketRegion(raw: MarketRegionRaw): MarketRegionItem {
  return {
    id: raw.id,
    regionName: raw.region_name,
    regionCode: raw.region_code ?? undefined,
    regionType: raw.region_type,
    description: raw.description ?? undefined,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? undefined,
  };
}

/** 将后端分页载荷转换为前端分页模型。 */
export function mapMarketRegionListPage(raw: MarketRegionListPageRaw): MarketRegionListPage {
  return {
    items: raw.items.map(mapMarketRegion),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 将后端区域范围行转换为前端范围模型。 */
export function mapMarketRegionScope(raw: MarketRegionScopeRaw): MarketRegionScopeItem {
  return {
    id: raw.id,
    regionId: raw.region_id,
    scopeType: raw.scope_type,
    country: raw.country ?? undefined,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    orgId: raw.org_id ?? undefined,
    orgUnitId: raw.org_unit_id ?? undefined,
    customerId: raw.customer_id ?? undefined,
    contactId: raw.contact_id ?? undefined,
    scopeNameSnapshot: raw.scope_name_snapshot ?? undefined,
    isExcluded: raw.is_excluded,
    isExcludedLabel: raw.is_excluded_label,
    createdAt: raw.created_at,
    remark: raw.remark ?? undefined,
  };
}

/** 将后端区域成员行转换为前端成员模型。 */
export function mapMarketRegionMember(raw: MarketRegionMemberRaw): MarketRegionMemberItem {
  return {
    id: raw.id,
    regionId: raw.region_id,
    userId: raw.user_id,
    userName: raw.user_name ?? undefined,
    memberRole: raw.member_role,
    canAssign: raw.can_assign,
    canEditScope: raw.can_edit_scope,
    canViewStats: raw.can_view_stats,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark ?? undefined,
  };
}

/** 将员工接口载荷转换为区域成员选择项。 */
export function mapStaffOption(raw: SysStaffRaw): MarketRegionStaffOption {
  return {
    id: raw.id,
    username: raw.username,
    displayName: raw.realname || raw.nickname || raw.username || `员工 ${raw.id}`,
    mainDeptName: raw.main_dept_name ?? undefined,
    disabled: raw.status !== 1,
  };
}

/** 将学校组织接口载荷转换为区域范围选择候选。 */
export function mapMarketRegionOrganizationOption(
  raw: MarketRegionOrganizationRaw,
): MarketRegionOrganizationOption {
  return {
    id: raw.id,
    orgName: raw.org_name,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
  };
}

/** 将院系专业接口载荷转换为区域范围选择候选。 */
export function mapMarketRegionOrganizationUnitOption(
  raw: MarketRegionOrganizationUnitRaw,
): MarketRegionOrganizationUnitOption {
  return {
    id: raw.id,
    orgId: raw.org_id,
    unitName: raw.unit_name,
    unitPathName: raw.unit_path_name ?? raw.unit_name,
    levelNo: raw.level_no ?? 0,
  };
}
